import ManualOrderConsigneeDTO from "@/dto/manualOrder/ManualOrderConsigneeDTO";
import { InjectBaseModalProps } from "@/hooks/Modal/ModalStore";
import { useGlobalStore } from "@/store/hooks";
import { Modal, Input, Cascader, Button, Table, message, InputNumber } from "antd";
import { Form } from "antd";
import type { TableColumnsType } from "antd";
import { observer } from "mobx-react-lite";
import React, { useEffect, useMemo } from "react";
import { batchManualAddOrder, OrderDto } from "@/services/api/manualOrder";
import { mobileRegExp, v1VirtualMobileRegExp, v2VirtualMobileRegExp } from "@/utils/RegExp";
import PaymentType from "@/enum/PaymentType";
import AutoLoadingButton from "@/components/AutoLoadingButton";
import { uuid } from "@/utils";

interface BatchInputModalProps extends InjectBaseModalProps {
    shopId: number;
    consignees: ManualOrderConsigneeDTO[];
    onDone: () => void;
}

interface BatchInputFormItem extends ManualOrderConsigneeDTO {
    productName?: string;
    productOriginalId?: string;
    skuProperties?: string;
    skuExternalId?: string;
    quantity?: number;
    price?: number;
    weight?: number;
    sellerRemarks?: string;
    originalId?: string;
    provinceCityDistrict?: number[];
}

interface BatchInputFormValues {
    consignees: BatchInputFormItem[];
}

const BatchInputModal: React.FC<BatchInputModalProps> = function (props) {
    const { open, onCancel, consignees, shopId, onDone } = props;
    const globalStore = useGlobalStore();
    const fhyAreas = globalStore.areaStore.fhyAreas;

    const [form] = Form.useForm<BatchInputFormValues>();

    useEffect(() => {
        if (open) {
            form.setFieldsValue({
                consignees: consignees.map((item) => ({
                    ...item,
                    provinceCityDistrict: [item.provinceCode, item.cityCode, item.districtCode],
                })),
            });
        }
    }, [open, consignees, form]);

    async function handleCreate() {
        let values;

        try {
            values = await form.validateFields();
        } catch {
            Modal.error({ title: "请检查收货人信息" });

            throw new Error("用户取消操作");
        }

        const { consignees: formConsignees } = values;

        const orders: OrderDto[] = [];

        for (const item of formConsignees) {
            const [provinceCode, cityCode, districtCode] = item.provinceCityDistrict || [];

            const splitMobile = item.mobile?.split("-");

            /** 手工录入订单的虚拟号：保存的时候需要转换格式保存，按这种格式保存：姓名(xxxx)，详细地址(分机号：xxxx) */
            const isVirtual = item.mobile && v1VirtualMobileRegExp.test(item.mobile);
            const virtualNo = isVirtual ? splitMobile?.[1] : "";
            const mergeConsigneeName = isVirtual ? item.name + `[${virtualNo}]` : item.name;
            const mergeConsigneeStreet = isVirtual ? item.street + `[${virtualNo}]` : item.street;
            const mergeConsigneeMobile = isVirtual ? splitMobile?.[0] : item.mobile;

            let orderItems;

            if (
                item.productName ||
                item.productOriginalId ||
                item.skuProperties ||
                item.skuExternalId ||
                item.quantity ||
                item.price ||
                item.weight
            ) {
                orderItems = [
                    {
                        title: item.productName,
                        productOriginalId: item.productOriginalId,
                        skuProperties: item.skuProperties,
                        skuExternalId: item.skuExternalId,
                        quantity: item.quantity,
                        price: item.price,
                        weight: item.weight,
                    },
                ];
            }

            orders.push({
                sellerRemarks: item.sellerRemarks,
                originalId: item.originalId,
                consigneeName: mergeConsigneeName,
                mobile: mergeConsigneeMobile,
                street: mergeConsigneeStreet,
                provinceCode,
                cityCode,
                districtCode,
                paymentType: PaymentType.OFFLINE,
                orderItems,
                telephone: item.telephone,
                zipCode: item.zipCode,
            });
        }

        const results = await batchManualAddOrder({ shopId, saveAndPrint: false, orders });

        let successCount = 0;
        let failCount = 0;

        results.forEach(({ success }) => {
            if (success) {
                successCount += 1;
            } else {
                failCount += 1;
            }
        });

        if (failCount === 0) {
            message.success(`成功批量添加 ${successCount} 条订单`);
            onCancel();
        } else {
            Modal.info({
                title: "批量处理结果",
                content: (
                    <div>
                        <p style={{ color: "#697861" }}>成功：{successCount} 条</p>
                        <p style={{ color: "#ff4d4f" }}>失败：{failCount} 条</p>
                        {successCount > 0 && <p>请在订单列表中检查已添加成功的订单。</p>}
                    </div>
                ),
                onOk: () => {
                    if (successCount > 0) {
                        onCancel();
                    }
                },
            });
        }

        onCancel();

        onDone();
    }

    const productValidator = (index: number) => async () => {
        const row = form.getFieldValue(["consignees", index]) || {};
        const { quantity, price, weight, productName, productOriginalId, skuProperties, skuExternalId } = row;

        const hasMeasure = [quantity, price, weight].some((value) => value !== undefined && value !== null);
        const hasProductInfo = [productName, productOriginalId, skuProperties, skuExternalId].some(
            (value) => typeof value === "string" && value.trim().length > 0,
        );

        if (hasMeasure) {
            if (!hasProductInfo) {
                return Promise.reject("至少填写一个商品信息");
            }
        }

        return Promise.resolve();
    };

    const columns: TableColumnsType<ManualOrderConsigneeDTO> = [
        {
            title: "姓名(必填)",
            width: 140,
            dataIndex: "name",
            key: "name",
            render: (_, record, index) => (
                <Form.Item name={["consignees", index, "name"]} rules={[{ required: true, message: "请输入姓名" }]}>
                    <Input placeholder="请输入姓名" />
                </Form.Item>
            ),
        },
        {
            title: "手机(必填)",
            dataIndex: "mobile",
            key: "mobile",
            render: (_, record, index) => (
                <Form.Item
                    name={["consignees", index, "mobile"]}
                    rules={[
                        {
                            validator(_, mobile) {
                                if (!mobile) return Promise.reject("手机号不能为空");

                                if (
                                    !mobileRegExp.test(mobile) &&
                                    !v1VirtualMobileRegExp.test(mobile) &&
                                    !v2VirtualMobileRegExp.test(mobile)
                                ) {
                                    return Promise.reject("请输入有效的手机号码");
                                }

                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <Input placeholder="请输入手机号" />
                </Form.Item>
            ),
        },
        {
            title: "省市区(必填)",
            width: 240,
            render: (_, record, index) => (
                <Form.Item
                    name={["consignees", index, "provinceCityDistrict"]}
                    rules={[
                        {
                            validator(_, value) {
                                if (!value?.length) {
                                    return Promise.reject("省市区为空");
                                }

                                if (value.length < 3) {
                                    return Promise.reject("缺失地址信息");
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <Cascader
                        options={fhyAreas}
                        fieldNames={{ label: "name", value: "id", children: "children" }}
                        placeholder="请选择省市区"
                        changeOnSelect
                    />
                </Form.Item>
            ),
        },
        {
            title: "详细地址(必填)",
            dataIndex: "street",
            width: 350,
            key: "street",
            render: (_, record, index) => (
                <Form.Item
                    name={["consignees", index, "street"]}
                    rules={[{ required: true, message: "请输入详细地址" }]}
                >
                    <Input placeholder="请输入详细地址" />
                </Form.Item>
            ),
        },
        {
            title: "商品名称(非必填)",
            dataIndex: "productName",
            key: "productName",
            render: (_, record, index) => (
                <Form.Item
                    name={["consignees", index, "productName"]}
                    dependencies={[
                        ["consignees", index, "quantity"],
                        ["consignees", index, "price"],
                        ["consignees", index, "weight"],
                        ["consignees", index, "productOriginalId"],
                        ["consignees", index, "skuProperties"],
                        ["consignees", index, "skuExternalId"],
                    ]}
                    rules={[{ validator: productValidator(index) }]}
                >
                    <Input placeholder="请输入商品名称" />
                </Form.Item>
            ),
        },
        {
            title: "商品编码(非必填)",
            dataIndex: "productOriginalId",
            key: "productOriginalId",
            render: (_, record, index) => (
                <Form.Item
                    name={["consignees", index, "productOriginalId"]}
                    dependencies={[
                        ["consignees", index, "quantity"],
                        ["consignees", index, "price"],
                        ["consignees", index, "weight"],
                        ["consignees", index, "productName"],
                        ["consignees", index, "skuProperties"],
                        ["consignees", index, "skuExternalId"],
                    ]}
                    rules={[{ validator: productValidator(index) }]}
                >
                    <Input placeholder="请输入商品编码" />
                </Form.Item>
            ),
        },
        {
            title: "销售属性(非必填)",
            dataIndex: "skuProperties",
            key: "skuProperties",
            render: (_, record, index) => (
                <Form.Item
                    name={["consignees", index, "skuProperties"]}
                    dependencies={[
                        ["consignees", index, "quantity"],
                        ["consignees", index, "price"],
                        ["consignees", index, "weight"],
                        ["consignees", index, "productName"],
                        ["consignees", index, "productOriginalId"],
                        ["consignees", index, "skuExternalId"],
                    ]}
                    rules={[{ validator: productValidator(index) }]}
                >
                    <Input placeholder="请输入销售属性" />
                </Form.Item>
            ),
        },
        {
            title: "SKU编码(非必填)",
            dataIndex: "skuExternalId",
            key: "skuExternalId",
            render: (_, record, index) => (
                <Form.Item
                    name={["consignees", index, "skuExternalId"]}
                    dependencies={[
                        ["consignees", index, "quantity"],
                        ["consignees", index, "price"],
                        ["consignees", index, "weight"],
                        ["consignees", index, "productName"],
                        ["consignees", index, "productOriginalId"],
                        ["consignees", index, "skuProperties"],
                    ]}
                    rules={[{ validator: productValidator(index) }]}
                >
                    <Input placeholder="请输入SKU编码" />
                </Form.Item>
            ),
        },
        {
            title: "数量(非必填)",
            dataIndex: "quantity",
            key: "quantity",
            render: (_, record, index) => (
                <Form.Item name={["consignees", index, "quantity"]}>
                    <InputNumber placeholder="请输入数量" min={1} precision={0} />
                </Form.Item>
            ),
        },
        {
            title: "单价(非必填)",
            dataIndex: "price",
            key: "price",
            render: (_, record, index) => (
                <Form.Item name={["consignees", index, "price"]}>
                    <InputNumber placeholder="请输入单价" min={0} precision={2} />
                </Form.Item>
            ),
        },
        {
            title: "重量(非必填)",
            dataIndex: "weight",
            key: "weight",
            render: (_, record, index) => (
                <Form.Item name={["consignees", index, "weight"]}>
                    <InputNumber placeholder="请输入重量" min={0} />
                </Form.Item>
            ),
        },
        {
            title: "备注(非必填)",
            dataIndex: "sellerRemarks",
            key: "sellerRemarks",
            render: (_, record, index) => (
                <Form.Item name={["consignees", index, "sellerRemarks"]}>
                    <Input placeholder="请输入备注" />
                </Form.Item>
            ),
        },
        {
            title: "订单编号(非必填)",
            dataIndex: "originalId",
            key: "originalId",
            render: (_, record, index) => (
                <Form.Item name={["consignees", index, "originalId"]}>
                    <Input placeholder="请输入订单编号" />
                </Form.Item>
            ),
        },
    ];

    const dataSource = useMemo(() => {
        return consignees.map((it) => ({ ...it, key: uuid() }));
    }, []);

    return (
        <Modal
            open={open}
            title={`确认"批量识别"导入信息`}
            onCancel={onCancel}
            footer={
                <>
                    <Button onClick={onCancel}>取 消</Button>

                    <AutoLoadingButton type="primary" onClick={handleCreate}>
                        确 定
                    </AutoLoadingButton>
                </>
            }
            width={1200}
        >
            <Form form={form}>
                <Table columns={columns} dataSource={dataSource} rowKey="key" pagination={false} scroll={{ x: 2500 }} />
            </Form>
        </Modal>
    );
};

export default observer(BatchInputModal);
